/** DESIGN §31.4: the team room's notices, so the portal re-reads what changed as it lands. */
import { addDays, mondayOf } from "./portal/model/days.js";
import {
  absencesPrefix,
  attentionKey,
  historyKey,
  liftsKey,
  loadKey,
  plansKey,
  previousWorkoutsKey,
  publishedKey,
  revalidate,
  revalidateAll,
  revalidatePrefix,
  trainingKey,
  weekKey,
  weeksKey,
} from "./portal/data.js";

export type Notice =
  | { kind: "draft"; week: string; revision: number; by?: string }
  | { kind: "notes"; week: string; by?: string }
  | { kind: "published" | "unpublished"; week: string; by?: string }
  | { kind: "workout"; athleteID: string; date: string; by?: string }
  | { kind: "training"; athleteID: string; date: string; by?: string }
  | { kind: "plans"; athleteID?: string; by?: string }
  | { kind: "absence"; athleteID: string; date: string; by?: string };

export type LiveEvent = Notice | { kind: "reconnect" };

export const PING_MS = 25_000;
export const MIN_BACKOFF_MS = 1_000;
export const MAX_BACKOFF_MS = 30_000;
/** The room closes a signed-out session's sockets with this; nothing reconnects until signed in again. */
export const SIGNED_OUT = 4001;

const listeners = new Set<(event: LiveEvent) => void>();
let started = false;
let socket: WebSocket | null = null;
let opened = false;
let attempt = 0;
let pingTimer: ReturnType<typeof setInterval> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

export function onLive(fn: (event: LiveEvent) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function emit(event: LiveEvent): void {
  for (const l of [...listeners]) {
    try {
      l(event);
    } catch {
      // One listener failing never keeps the notice from the others.
    }
  }
}

/** The cached reads a notice makes stale, revalidated so each page keeps showing while it reads again. */
export function applyNotice(notice: Notice): void {
  switch (notice.kind) {
    case "workout": {
      if (typeof notice.date !== "string") break;
      const m = mondayOf(notice.date);
      const next = addDays(m, 7);
      const keys = [weekKey(m), weekKey(next), attentionKey(m), previousWorkoutsKey(next), liftsKey, loadKey];
      if (notice.athleteID) keys.push(historyKey(notice.athleteID));
      revalidate(...keys);
      break;
    }
    case "training":
      if (typeof notice.date === "string") revalidate(trainingKey(notice.date));
      break;
    case "plans":
      revalidatePrefix(weekKey(""));
      break;
    case "absence":
      revalidatePrefix(absencesPrefix);
      break;
    case "published":
    case "unpublished":
      if (typeof notice.week !== "string") break;
      revalidate(publishedKey(notice.week), weeksKey, plansKey, weekKey(notice.week), weekKey(addDays(notice.week, 7)));
      break;
    case "draft":
      revalidate(plansKey);
      break;
    case "notes":
      break;
  }
}

/** A notice as it arrives: the cache first, then whoever listens (an open draft, Coach notes). */
export function handleNotice(notice: Notice): void {
  applyNotice(notice);
  emit(notice);
}

function liveURL(): string {
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/web/api/live`;
}

function connect(): void {
  clearTimeout(retryTimer);
  retryTimer = undefined;
  if (!started || socket || typeof WebSocket === "undefined") return;
  let ws: WebSocket;
  try {
    ws = new WebSocket(liveURL());
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;
  ws.onopen = () => {
    if (socket !== ws) return;
    attempt = 0;
    clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, PING_MS);
    if (opened) {
      revalidateAll();
      emit({ kind: "reconnect" });
    }
    opened = true;
  };
  ws.onmessage = (event: MessageEvent) => {
    if (socket !== ws || typeof event.data !== "string" || event.data === "pong") return;
    let notice: unknown;
    try {
      notice = JSON.parse(event.data);
    } catch {
      return;
    }
    if (notice && typeof notice === "object" && typeof (notice as { kind?: unknown }).kind === "string") handleNotice(notice as Notice);
  };
  ws.onclose = (event: CloseEvent) => {
    if (socket !== ws) return;
    socket = null;
    clearInterval(pingTimer);
    if (event.code === SIGNED_OUT) {
      started = false;
      return;
    }
    scheduleReconnect();
  };
}

function scheduleReconnect(): void {
  if (!started) return;
  const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** attempt);
  attempt++;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(connect, base / 2 + (Math.random() * base) / 2);
}

function onVisible(): void {
  if (document.visibilityState === "visible" && started && !socket) {
    attempt = 0;
    connect();
  }
}

/** Once signed in; again after a sign-out closed it. */
export function startLive(): void {
  if (started) return;
  started = true;
  opened = false;
  attempt = 0;
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
  connect();
}

export function stopLive(): void {
  started = false;
  opened = false;
  attempt = 0;
  clearTimeout(retryTimer);
  retryTimer = undefined;
  clearInterval(pingTimer);
  if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
  const ws = socket;
  socket = null;
  if (ws) {
    ws.onopen = ws.onmessage = ws.onclose = null;
    try {
      ws.close(1000);
    } catch {
      // Closing a socket still connecting can throw in some browsers; it is dropped either way.
    }
  }
}
